import React, { useEffect, useState, useCallback } from 'react';
import { menuApi, ordersApi, MenuItem } from '../api/client';
import { AuthState } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────
interface CartItem {
  item: MenuItem;
  quantity: number;
}

interface KioskOrderPageProps {
  auth: AuthState;
}

const TAX_RATE = 0.0825;

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n: number) => `$${n.toFixed(2)}`;

// ── Component ──────────────────────────────────────────────────────────────────
export const KioskOrderPage: React.FC<KioskOrderPageProps> = ({ auth }) => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [notes, setNotes] = useState('');

  // ── Fetch Menu ───────────────────────────────────────────────────────────────
  const fetchMenu = useCallback(async () => {
    setLoading(true);
    try {
      const { items: menuItems, categories: cats } = await menuApi.list(auth.restaurantId);
      const available = menuItems.filter((i) => i.isAvailable);
      setItems(available);
      setCategories(cats.filter((c) => available.some((i) => i.category === c)));
      setActiveCategory((prev) => prev || cats[0] || '');
    } catch (e) {
      console.error('Failed to load menu', e);
    } finally {
      setLoading(false);
    }
  }, [auth.restaurantId]);

  useEffect(() => { void fetchMenu(); }, [fetchMenu]);

  // ── Cart ─────────────────────────────────────────────────────────────────────
  const addToCart = (item: MenuItem) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.item.itemId === item.itemId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
        return next;
      }
      return [...prev, { item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.item.itemId === itemId);
      if (idx < 0) return prev;
      const next = [...prev];
      if (next[idx].quantity > 1) {
        next[idx] = { ...next[idx], quantity: next[idx].quantity - 1 };
      } else {
        next.splice(idx, 1);
      }
      return next;
    });
  };

  const clearCart = () => { setCart([]); setCustomerName(''); setNotes(''); };

  const cartCount = (itemId: string) =>
    cart.find((c) => c.item.itemId === itemId)?.quantity ?? 0;

  const subtotal = cart.reduce((s, c) => s + c.item.price * c.quantity, 0);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  // ── Place Order ──────────────────────────────────────────────────────────────
  const placeOrder = async () => {
    if (cart.length === 0) return;
    setPlacing(true);
    try {
      const order = await ordersApi.create({
        restaurantId: auth.restaurantId,
        kioskId: 'kitchen-dashboard',
        items: cart.map((c) => ({
          itemId: c.item.itemId,
          name: c.item.name,
          price: c.item.price,
          quantity: c.quantity,
        })),
        paymentMethod: 'PAY_AT_COUNTER',
        customerName: customerName.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setOrderSuccess(`Order #${order.orderId.slice(-6).toUpperCase()} placed!`);
      clearCart();
      setShowCheckout(false);
      setTimeout(() => setOrderSuccess(null), 5000);
    } catch (e) {
      console.error('Failed to place order', e);
    } finally {
      setPlacing(false);
    }
  };

  // ── Visible items ────────────────────────────────────────────────────────────
  const visibleItems = activeCategory
    ? items.filter((i) => i.category === activeCategory)
    : items;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-52px)] overflow-hidden bg-gray-950 text-white">

      {/* ── LEFT: Category sidebar ────────────────────────────────── */}
      <aside className="w-44 shrink-0 flex flex-col gap-1 p-3 bg-gray-900 border-r border-white/5 overflow-y-auto">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest px-2 mb-1">Categories</p>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`text-left px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeCategory === cat
                ? 'bg-indigo-600 text-white'
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            {cat}
          </button>
        ))}
      </aside>

      {/* ── CENTER: Items grid ────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto p-5">
        {/* Success toast */}
        {orderSuccess && (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-xl font-bold shadow-xl animate-bounce">
            ✅ {orderSuccess}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <h2 className="text-base font-bold text-gray-400 mb-4 uppercase tracking-widest">
              {activeCategory}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {visibleItems.map((item) => {
                const qty = cartCount(item.itemId);
                return (
                  <div
                    key={item.itemId}
                    onClick={() => addToCart(item)}
                    className="group cursor-pointer bg-gray-900 rounded-2xl overflow-hidden border border-white/5 hover:border-indigo-500/60 hover:shadow-lg hover:shadow-indigo-500/10 transition-all active:scale-95"
                  >
                    {/* Image */}
                    <div className="relative aspect-[4/3] bg-gray-800">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl">🍽️</div>
                      )}
                      {qty > 0 && (
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-black shadow-lg">
                          {qty}
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-3">
                      <p className="font-bold text-sm leading-tight text-white">{item.name}</p>
                      {item.description && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm font-black text-indigo-400">{fmt(item.price)}</span>
                        {item.calories && (
                          <span className="text-[10px] text-gray-600">{item.calories} cal</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* ── RIGHT: Cart ───────────────────────────────────────────── */}
      <aside className="w-72 shrink-0 flex flex-col bg-gray-900 border-l border-white/5">
        <div className="px-4 pt-4 pb-2 border-b border-white/5 flex items-center justify-between">
          <span className="font-black text-sm">🛒 Order</span>
          {cart.length > 0 && (
            <button onClick={clearCart} className="text-[11px] text-gray-500 hover:text-red-400 transition-colors">
              Clear
            </button>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-600 text-sm">
              <span className="text-3xl mb-2">🛒</span>
              Add items from the menu
            </div>
          ) : (
            cart.map((c) => (
              <div key={c.item.itemId} className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{c.item.name}</p>
                  <p className="text-xs text-gray-500">{fmt(c.item.price)} each</p>
                </div>
                {/* Qty controls */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFromCart(c.item.itemId); }}
                    className="w-6 h-6 rounded-full bg-white/10 hover:bg-red-500/30 text-sm font-bold flex items-center justify-center transition-colors"
                  >−</button>
                  <span className="text-sm font-black w-5 text-center">{c.quantity}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); addToCart(c.item); }}
                    className="w-6 h-6 rounded-full bg-white/10 hover:bg-indigo-500/40 text-sm font-bold flex items-center justify-center transition-colors"
                  >+</button>
                </div>
                <span className="text-sm font-bold text-indigo-300 w-12 text-right">
                  {fmt(c.item.price * c.quantity)}
                </span>
              </div>
            ))
          )}
        </div>

        {/* Totals */}
        {cart.length > 0 && (
          <div className="border-t border-white/5 p-4 space-y-1">
            <div className="flex justify-between text-sm text-gray-400">
              <span>Subtotal</span><span>{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span>Tax (8.25%)</span><span>{fmt(tax)}</span>
            </div>
            <div className="flex justify-between text-base font-black text-white mt-2">
              <span>Total</span><span>{fmt(total)}</span>
            </div>
            <button
              onClick={() => setShowCheckout(true)}
              className="mt-3 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 font-black text-sm transition-all"
            >
              Place Order →
            </button>
          </div>
        )}
      </aside>

      {/* ── Checkout Modal ───────────────────────────────────────── */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-sm p-6 border border-white/10 shadow-2xl">
            <h3 className="font-black text-lg mb-4">Confirm Order</h3>

            <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
              {cart.map((c) => (
                <div key={c.item.itemId} className="flex justify-between text-sm">
                  <span className="text-gray-300">{c.quantity}× {c.item.name}</span>
                  <span className="text-indigo-300 font-bold">{fmt(c.item.price * c.quantity)}</span>
                </div>
              ))}
              <div className="border-t border-white/10 pt-2 flex justify-between font-black">
                <span>Total</span><span className="text-indigo-400">{fmt(total)}</span>
              </div>
            </div>

            <input
              type="text"
              placeholder="Customer name (optional)"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 mb-3"
            />
            <textarea
              placeholder="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-indigo-500 mb-4 resize-none"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowCheckout(false)}
                className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={placeOrder}
                disabled={placing}
                className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-sm font-black transition-all"
              >
                {placing ? 'Placing…' : '✓ Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
